import type { PageMetaDataValues } from "@app/types/PageMetaData";
import {
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  getSectionOgImageUrl,
} from "@config/ogImageConstants";
import { capitalize } from "@helper/stringTransform";
import type { GADataProps } from "@typings/GA";
import type { MetaTagsProps } from "@typings/MetaTags";

export type SectionMetaDataProps = {
  sectionName: string;
  parentSection: string | undefined;
  paywall?: string | null;
  isTerkini?: boolean;
};

export function SectionMetaData({
  sectionName,
  parentSection,
  paywall = null,
  isTerkini = false,
}: SectionMetaDataProps): PageMetaDataValues {
  let capitalizeSectionName = capitalize(sectionName.replace("-", " "));
  if (paywall != null || sectionName != "gaya-hidup") {
    capitalizeSectionName = capitalizeSectionName.replace(" ", "_");
  }

  let metaTitle = `Berita ${capitalizeSectionName} - Berita Terkini & Popular | Beritaharian.sg`;
  let metaDescription: string | undefined =
    `Berita ${capitalizeSectionName} - Baca dan dapatkan Berita ${capitalizeSectionName} terkini & popular hanya di BeritaHarian.sg`;
  if (isTerkini) {
    metaTitle = "Berita Terkini Singapura & Dunia Hari Ini | Berita Harian";
    metaDescription =
      "Ikuti perkembangan berita terkini hari ini menerusi kemas kini langsung Berita Harian. Baca laporan berita hari ini mengenai situasi semasa di Singapura dan dunia.";
  } else if (paywall != null) {
    metaTitle =
      "BeritaHarian - Berita Terkini, Singapura, Ekoniaga, Dunia & Sukan";
    switch (sectionName) {
      case "akses-percuma":
        metaDescription =
          "Berita Harian Singapura - Dapatkan berita percuma, laporan eksklusif, analisis mendalam dan kandungan berbilang media mengenai Singapura, Asia dan dunia.";
        break;
      case "premium-articles":
        metaDescription =
          "Berita Harian Singapura - Dapatkan berita premium, laporan eksklusif, analisis mendalam dan kandungan berbilang media mengenai Singapura, Asia dan dunia.";
        break;
      case "registered":
        metaDescription =
          "Berita Harian Singapura - Daftar diri untuk dapatkan berita premium, laporan eksklusif, analisis mendalam dan kandungan berbilang media mengenai Singapura, Asia dan dunia.";
        break;
      default:
        metaDescription = undefined;
        break;
    }
  } else if (parentSection !== undefined) {
    metaTitle = `Berita ${capitalize(parentSection)} - Berita Terkini & Popular | Beritaharian.sg`;
    switch (sectionName) {
      case "mahligai":
        metaDescription =
          "Mahligai Gah! - Baca dan dapatkan berita perkahwinan dan rumah tangga terkini & popular hanya di BeritaHarian.sg";
        break;
      case "wanita":
        metaDescription =
          "Wanita Gah! - Baca dan dapatkan berita Gaya Artistik Hiburan Wanita Gah! terkini & popular hanya di BeritaHarian.sg";
        break;
      case "hiburan":
        metaDescription =
          "Hiburan Gah! - Baca dan dapatkan berita hiburan terkini & popular hanya di BeritaHarian.sg";
        break;
      default:
        metaDescription = `${capitalizeSectionName} ${parentSection}! Baca dan dapatkan berita ${capitalizeSectionName} terkini & popular hanya di BeritaHarian.sg`;
        break;
    }
  } else if (sectionName == "gen-g") {
    metaTitle = "Gen G, Berita - Beritaharian.sg";
    metaDescription = "Dapatkan bahan pendidikan untuk pelajar sekolah";
  }

  const parentSectionValue = parentSection?.toLowerCase().replace(" ", "-");
  const sectionNameValue = sectionName.toLowerCase();
  const parentSectionExists =
    parentSectionValue && parentSectionValue !== sectionNameValue;
  const slug = isTerkini
    ? "/terkini"
    : paywall != null
      ? `/${sectionNameValue}`
      : parentSectionExists
        ? `/${parentSectionValue}/${sectionNameValue}`
        : `/${sectionNameValue}`;

  const ogImageUrl = isTerkini
    ? getSectionOgImageUrl("terkini")
    : getSectionOgImageUrl(sectionNameValue, parentSectionValue);

  const metaTags: MetaTagsProps = {
    title: metaTitle,
    description: metaDescription,
    slug: slug,
    ogImage: {
      url: ogImageUrl,
      width: DEFAULT_OG_IMAGE_WIDTH,
      height: DEFAULT_OG_IMAGE_HEIGHT,
    },
  };

  const level2 = parentSection
    ? parentSection.replace("-", " ")
    : sectionName.replace("-", " ");

  const gaData: GADataProps = {
    level2: level2,
    title: `${capitalizeSectionName}_Index`,
    section: level2,
    chapter1: parentSection && capitalizeSectionName,
    contentcat: "1",
    contenttype: "listing",
  };

  return {
    gaData,
    metaTags,
  };
}
